import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle,
  Circle,
  UserCheck,
  BookOpen,
  Trophy,
  BarChart3,
  Users,
  Rocket,
  X,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from "lucide-react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  icon: typeof CheckCircle;
  completed: boolean;
  path: string;
  actionLabel: string;
}

interface QuickStartGuideProps {
  user: any;
  dashboardStats: any;
  userAchievements: any[];
  readingStats: any;
}

export function QuickStartGuide({
  user,
  dashboardStats,
  userAchievements,
  readingStats,
}: QuickStartGuideProps) {
  const [, setLocation] = useLocation();
  const userId = user?.id || "";

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!userId) return;
    try {
      if (localStorage.getItem(`quickstart_dismissed_${userId}`) === "true") {
        setDismissed(true);
      }
    } catch {}
  }, [userId]);

  // Open on the very first visit, folded to one line after that. The checklist stays one click away.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return !!userId && localStorage.getItem(`quickstart_seen_${userId}`) === "true"; } catch { return false; }
  });
  useEffect(() => {
    if (!userId) return;
    try { localStorage.setItem(`quickstart_seen_${userId}`, "true"); } catch {}
  }, [userId]);

  const isProfileComplete = useMemo(() => {
    if (!user) return false;
    return !!(
      (user as any).gender &&
      (user as any).dateOfBirth &&
      (user as any).parentFirstName &&
      (user as any).parentLastName &&
      (user as any).parentEmail &&
      (user as any).parentPhone
    );
  }, [user]);

  const hasAttemptedQuiz = useMemo(() => {
    const stats = dashboardStats || {};
    return (stats.totalQuestions || 0) > 0;
  }, [dashboardStats]);

  const hasVisitedStudyPlan = useMemo(() => {
    if (!userId) return false;
    try { return localStorage.getItem(`quickstart_visited_study-plan_${userId}`) === "true"; } catch { return false; }
  }, [userId]);

  const hasVisitedLeaderboard = useMemo(() => {
    if (!userId) return false;
    try { return localStorage.getItem(`quickstart_visited_leaderboard_${userId}`) === "true"; } catch { return false; }
  }, [userId]);

  const hasVisitedProgressReport = useMemo(() => {
    if (!userId) return false;
    try { return localStorage.getItem(`quickstart_visited_progress-report_${userId}`) === "true"; } catch { return false; }
  }, [userId]);

  const hasEarnedBadge = useMemo(() => {
    return (userAchievements || []).length > 0;
  }, [userAchievements]);

  if (!userId) return null;

  const items: ChecklistItem[] = useMemo(
    () => [
      {
        id: "profile",
        title: "Complete Your Profile",
        description: "Add your school and parent details for prize delivery",
        icon: UserCheck,
        completed: isProfileComplete,
        path: "/settings",
        actionLabel: "Go to Settings",
      },
      {
        id: "quiz",
        title: "Try Your First Practice Quiz",
        description: "Answer questions to earn XP and level up",
        icon: BookOpen,
        completed: hasAttemptedQuiz,
        path: "/learning",
        actionLabel: "Start Practising",
      },
      {
        id: "study-plan",
        title: "Check Your Study Plan",
        description: "See personalised topic recommendations based on your progress",
        icon: BarChart3,
        completed: hasVisitedStudyPlan,
        path: "/study-plan",
        actionLabel: "View Plan",
      },
      {
        id: "leaderboard",
        title: "Visit the Leaderboard",
        description: "See how you rank against other students in your grade",
        icon: Users,
        completed: hasVisitedLeaderboard,
        path: "/leaderboard",
        actionLabel: "See Rankings",
      },
      {
        id: "progress-report",
        title: "View Your Progress Report",
        description: "Check your detailed performance breakdown by subject and topic",
        icon: BarChart3,
        completed: hasVisitedProgressReport,
        path: "/progress-report",
        actionLabel: "View Report",
      },
      {
        id: "badge",
        title: "Earn Your First Badge",
        description: "Complete challenges and milestones to unlock badges",
        icon: Trophy,
        completed: hasEarnedBadge,
        path: "/learning",
        actionLabel: "Start Earning",
      },
    ],
    [
      isProfileComplete,
      hasAttemptedQuiz,
      hasVisitedStudyPlan,
      hasVisitedLeaderboard,
      hasVisitedProgressReport,
      hasEarnedBadge,
    ]
  );

  const completedCount = items.filter((i) => i.completed).length;
  const totalCount = items.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);
  const allComplete = completedCount === totalCount;

  if (dismissed) return null;

  const handleNavigate = (item: ChecklistItem) => {
    if (item.id === "study-plan" || item.id === "leaderboard" || item.id === "progress-report") {
      try { localStorage.setItem(`quickstart_visited_${item.id}_${userId}`, "true"); } catch {}
    }
    setLocation(item.path);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(`quickstart_dismissed_${userId}`, "true"); } catch {}
    setDismissed(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.4 }}
    >
      <Card data-testid="card-quick-start-guide">
        <CardHeader className="flex flex-row items-center justify-between gap-2 p-4 md:p-5 pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Rocket className="w-5 h-5 text-primary flex-shrink-0" />
            <CardTitle className="text-base md:text-lg font-semibold">
              {allComplete ? "You're All Set!" : "Quick Start Guide"}
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              {completedCount}/{totalCount} completed
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setCollapsed(!collapsed)}
              data-testid="button-toggle-quickstart"
            >
              {collapsed ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleDismiss}
              data-testid="button-dismiss-quickstart"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-4 md:p-5 pt-0 space-y-4">
          <div className="space-y-1.5">
            <Progress value={progressPercent} className="h-1.5" />
            <p className="text-xs text-muted-foreground">
              {allComplete
                ? "Great job! You've explored all the key features."
                : `${progressPercent}% complete, keep going!`}
            </p>
          </div>

          {collapsed && !allComplete && (() => {
            const next = items.find((i) => !i.completed);
            if (!next) return null;
            return (
              <div className="flex items-center justify-between gap-3 text-sm" data-testid="quickstart-next">
                <span className="min-w-0 truncate">Next: <span className="font-medium">{next.title}</span></span>
                <Button size="sm" variant="outline" className="flex-shrink-0" onClick={() => handleNavigate(next)}>{next.actionLabel}</Button>
              </div>
            );
          })()}

          <AnimatePresence>
            {!collapsed && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <ul className="space-y-2">
                  {items.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <motion.li
                        key={item.id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`flex items-center gap-3 p-3 rounded-md transition-colors ${
                          item.completed
                            ? "bg-muted/50"
                            : "bg-muted/20 hover-elevate"
                        }`}
                        data-testid={`quickstart-item-${item.id}`}
                      >
                        <div className="flex-shrink-0">
                          {item.completed ? (
                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <Circle className="w-5 h-5 text-muted-foreground/40" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-medium ${
                              item.completed
                                ? "line-through text-muted-foreground"
                                : ""
                            }`}
                          >
                            {item.title}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {item.description}
                          </p>
                        </div>

                        {!item.completed && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleNavigate(item)}
                            className="flex-shrink-0"
                            data-testid={`quickstart-action-${item.id}`}
                          >
                            {item.actionLabel}
                          </Button>
                        )}
                      </motion.li>
                    );
                  })}
                </ul>

                {allComplete && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mt-4 text-center"
                  >
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-yellow-500" />
                      <p className="font-medium text-sm">
                        You've completed the Quick Start Guide!
                      </p>
                      <Sparkles className="w-5 h-5 text-yellow-500" />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDismiss}
                    >
                      Dismiss Guide
                    </Button>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
}
